import { event } from 'react-ga';
import ga4 from 'react-ga4';

let TRACKING_ID = 'G-J4T5WWDLBM'; //local tag Id
const isProduction = process.env.NODE_ENV === 'production';
let isLocal = false;

if (typeof window !== 'undefined') {
  const hostName = window.location.hostname.toLowerCase();
  if (hostName.includes('local')) {
    TRACKING_ID = 'G-J4T5WWDLBM';
    isLocal = true;
  } else if (hostName.includes('dev')) {
    TRACKING_ID = 'G-VH22LGZMB8';
  } else if (hostName.includes('qa')) {
    TRACKING_ID = 'G-NFK6TF1NK4';
  } else if (hostName.includes('next.eepass.org')) {
    TRACKING_ID = 'G-MHYM2Q89HN';
  }
}

const initParams = { 
    //testMode: true //!isProduction 
    trackingId: TRACKING_ID,
    send_page_view: false,
    gaOptions: {
      debug_mode: isLocal,
    },
    gtagOptions: {
      debug_mode: isLocal,
    },
  };

export const init = () => ga4.initialize([
  initParams
])

// Keep track of the last set userId
let _currentUserId = null;
// for dedupe
let _lastSentPage = { path: null, ts: 0 };

export const SetUserProperties = (userInfo, globalConfig) => {
  if (!window.gtag || !userInfo) {
    console.warn('GA4 not ready or no user info, cannot set user properties');
    return;
  }

  if (!globalConfig || !userInfo) {
    console.warn('globalConfig not ready or no user info, cannot set user properties');
    return;
  }

  //console.info('SetUserProperties GA4: ', JSON.stringify(userInfo));
  const userTitle = globalConfig.observationConfig.UserPositionAlternativeMap.find(x => x.UserPosition === userInfo.UserPositionType).Name;

  const newUserProps = {
      //user_type_name: userInfo.UserTypeName,
      user_roles: userInfo.Roles,
      user_title: userTitle ?? userInfo.Title,
      org_name: globalConfig.OrganizationNames,
      district_name: globalConfig.DistrictNames,
      school_name: globalConfig.SchoolNames,
      user_profile_id: userInfo.UserId,
    };
  //console.info('newUserProps GA4: ', JSON.stringify(newUserProps));

  setGAUserId(userInfo.UserId);
  ga4.gtag('set', 'user_properties', {
    ...newUserProps,
  });
};
 
export const TrackPageView = async (location, appUserMgr) => {
  const path = `${location?.pathname ?? ''}${location?.search ?? ''}`;
  // dedupe: ignore if same path was just sent
  if (_lastSentPage.path === path && (Date.now() - _lastSentPage.ts) < 8000) {
    console.info('Path already sent, skipping GA4 page view:', path);
    return;
  }

  const ready = await waitForGa4Ready();
  if (!ready) {
    console.warn('GA4 not ready, cannot track page view');
    return;
  }

  const globalConfig = await waitForGlobalState(appUserMgr);
  const userInfo = globalConfig?.userProfile;

  if (userInfo) {
    SetUserProperties(userInfo, globalConfig);
  } else {
    console.info('GA4 TrackPageView: userInfo is NULL');
    //setGAUserId(undefined); // Do not unset - probably why GA4 reports ANONYMOUS in secure pages
  } 

  console.info('GA4 TrackPageView: ', path);
  ga4.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
    // event_source_func: 'TrackPageView',
  });

  // update last sent
  _lastSentPage = { path, ts: Date.now() };
}

export const HandleLogout = () => {
  console.info('GA4 HandleLogout');
  setGAUserId(undefined);

  ga4.gtag('set', 'user_properties', {
    user_type_name: undefined,
    user_roles: undefined,
    user_title: undefined,
    org_name: undefined,
    district_name: undefined,
    school_name: undefined,
    user_profile_id: undefined,
  });

  ga4.gtag('config', TRACKING_ID, initParams);
}

export const AddEvent = async (eventTypeEnum, value, content, mgr) => {
  const ready = await waitForGa4Ready();
  if (!ready) return;

  const gAppUserState = await waitForGlobalState(mgr);
  const userInfo = gAppUserState?.userProfile;

  let eventData = eventTypeObjects[eventTypeEnum];
  if (!eventData) {
    return;
  }

  eventData.value = value;

  if (content) {
    if (eventData.category.toLowerCase() === "content") {
      eventData = {
        ...eventData,
        content_type_name: content.ContentTypeName,
        label: content.Title,
        page_location: window.location.href,
        page_title: document.title,
        //event_source_func: 'AddEvent.content',
      }
    }

    if (eventTypeEnum == EventTypeEnum.Content_Accessed) {
      eventData = {
        ...eventData,
        content_path: getContentPath(content),
        content_type_name: content.ContentTypeName,
        title: content.Title,
        mime_type: content.MimeType,
        file_type: content.FileExt,
        page_location: window.location.href,
        page_title: content.title,
        //event_source_func: 'AddEvent.Content_Accessed',
      }
    }
  } else {
    eventData = {
      ...eventData,
      page_path: window.location.pathname,
      page_location: window.location.href,
      page_title: document.title,
      //event_source_func: 'AddEvent.contentNULL',
    }
  }

  if (userInfo) {
    SetUserProperties(userInfo, gAppUserState);
  } else {
    const gAuthState = mgr.GetGlobalAuthState();
    if (!gAuthState?.isAuthenticated){
      setGAUserId(undefined);
    }
  }

  if (eventTypeEnum == EventTypeEnum.Auth_Logout) {
    HandleLogout();
  }

  ga4.event(eventData.category, eventData);
}

const mapUserProperties = (userInfo, userOrg, userDist, userSchool) => ({
  //user_type_name: userInfo.UserTypeName.get(),
  user_roles: userInfo.Roles.get(),
  user_title: userInfo.Title.get(),
  org_name: userOrg,
  district_name: userDist,
  school_name: userSchool,
  user_profile_id: userInfo.UserId.get(),
});

const getContentPath = (content) => {
  const path = window.location.protocol + "//" + window.location.host;
  return path + "/content/?k=" + content.KeyId;
}

export const waitForGa4Ready = async (timeoutMs = 5000) => {
  const start = Date.now();
  while (
    (!window.gtag || typeof window.gtag !== 'function') &&
    Date.now() - start < timeoutMs
  ) {
    await new Promise(res => setTimeout(res, 50));
  }

  if (!window.gtag) {
    console.warn('GA4 failed to initialize within timeout');
    return false;
  }
  return true;
};

export const waitForGlobalState = async (appUserMgr, timeoutMs = 5000) => {
  const start = Date.now();
  var globalState = appUserMgr?.GetGlobalState();

  while (
    (!globalState) &&
    Date.now() - start < timeoutMs
  ) {
    await new Promise(res => setTimeout(res, 50));
  }

  if (!globalState) {
    console.warn('GlobalState failed to initialize within timeout');
    return null;
  }
  return globalState;
};

export const setGAUserId = (userId) => {
  if (_currentUserId !== userId) {
    ga4.set({ userId: userId ?? undefined });
    _currentUserId = userId ?? null;
    //console.info('GA4 userId updated:', _currentUserId);
  } else {
    //console.info('GA4 userId already set:', _currentUserId);
  }
};

export const EventTypeEnum = {
    Auth_Login: 1,
    Auth_Logout: 2,
    Content_Download: 3,
    Content_Accessed: 4,
    Content_Shared: 5,
    View_Report: 6,
};

export const eventTypeObjects = {
  [EventTypeEnum.Auth_Login]: { category: "auth", action: "User Logged In" },
  [EventTypeEnum.Auth_Logout]: { category: "auth", action: "User Logged Out" },
  [EventTypeEnum.Content_Download]: { category: "content", action: "Download" },
  [EventTypeEnum.Content_Accessed]: { category: "content", action: "View" },
  [EventTypeEnum.Content_Shared]: { category: "content", action: "Shared" },
  [EventTypeEnum.View_Report]: { category: "reporting", action: "View" }
};

export { ga4 };